import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import {
  accountRequiredMessage,
  type ContractService,
  errors,
  paxRoute,
  type RouteAuth,
  staffAuth,
  unauthorizedMessage,
} from "../../common";
import {
  BasemapBlobHeaders,
  BasemapBlobParams,
  BasemapDraftParams,
  BasemapDraftResponse,
  BasemapFileParams,
  BasemapFileResponse,
  BasemapJob,
  BasemapJobParams,
  BasemapManifestParams,
  BasemapParams,
  BasemapQuery,
  BasemapResponse,
  CreateBasemapBlobsRequest,
  CreateBasemapBlobsResponse,
  CreateBasemapRequest,
  DeleteBasemapFileRequest,
  KeepBasemapBlobsRequest,
  PublishBasemapRequest,
} from "./schemas";

export const service = {
  group: "basemaps",
  version: "v1alpha1",
  stage: "alpha",
  title: "Pax Basemaps API",
  description:
    "Start and publish basemap drafts, store their blobs, read published manifests, and take down published files.",
} as const satisfies ContractService;

const account: RouteAuth = { kind: "account" };

const accountErrors = {
  UNAUTHORIZED: { message: unauthorizedMessage },
  FORBIDDEN: { message: accountRequiredMessage },
} as const;

const noSuchDraft = {
  NOT_FOUND: {
    message: "There's no draft with that id on a basemap the caller owns or collaborates on.",
  },
} as const;

/** A draft blob's bytes: all of them, or `206` with the one range asked for. */
const BlobResponse = z.object({
  status: z.union([z.literal(200), z.literal(206)]),
  headers: z.record(z.string(), z.string()).optional(),
  body: z.instanceof(ReadableStream),
});

// Starting a draft

export const create = oc
  .meta(
    paxRoute({ auth: account, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/basemaps/v1alpha1",
      operationId: "basemaps.create",
      summary: "Start a basemap draft.",
      description:
        "Starts a draft version and its room: a new world, a new draft of a basemap the caller can edit, a copy of a published version, or an uploaded package. Name and describe it with the Assets API.",
      inputStructure: "detailed",
      successStatus: 201,
      successDescription: "The new draft.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...accountErrors,
    NOT_FOUND: { message: "There's no published basemap version with that id the caller can use." },
  })
  .input(z.object({ body: CreateBasemapRequest }))
  .output(BasemapDraftResponse);

// Published basemaps

export const get = oc
  .meta(
    paxRoute({ auth: { kind: "public" }, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/basemaps/v1alpha1/{id}",
      operationId: "basemaps.get",
      summary: "Get a published basemap.",
      description:
        "Returns a published version's manifest and every file it lists: the one asked for, or the asset's live version. A basemap a moderator took down is its owner's alone, read through signed URLs.",
      inputStructure: "detailed",
      successDescription: "The basemap version.",
    }),
  )
  .errors({
    ...errors.badRequest,
    NOT_FOUND: {
      message:
        "No published basemap version has that id, or a moderator took it down and the caller isn't its owner.",
    },
  })
  .input(z.object({ params: BasemapParams, query: BasemapQuery }))
  .output(BasemapResponse);

export const getByManifest = oc
  .meta(
    paxRoute({ auth: { kind: "public" }, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/basemaps/v1alpha1/manifests/{sha256}",
      operationId: "basemaps.manifests.get",
      summary: "Find the basemap a manifest belongs to.",
      description:
        "A manifest's hash names one basemap version. This returns the basemap version with that manifest, including a version a moderator removed. A basemap a moderator took down whole is its owner's alone, read through signed URLs.",
      inputStructure: "detailed",
      successDescription: "The basemap version.",
    }),
  )
  .errors({
    ...errors.badRequest,
    NOT_FOUND: {
      message:
        "No basemap version has that manifest, or a moderator took it down and the caller isn't its owner.",
    },
  })
  .input(z.object({ params: BasemapManifestParams }))
  .output(BasemapResponse);

// Drafts

export const getDraft = oc
  .meta(
    paxRoute({ auth: account, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/basemaps/v1alpha1/{id}/drafts/{versionId}",
      operationId: "basemaps.drafts.get",
      summary: "Get a basemap draft.",
      description: "Reads the draft's version row, and its room's document and commit count.",
      inputStructure: "detailed",
      successDescription: "The draft.",
    }),
  )
  .errors({ ...errors.badRequest, ...accountErrors, ...noSuchDraft })
  .input(z.object({ params: BasemapDraftParams }))
  .output(BasemapDraftResponse);

export const createBlobs = oc
  .meta(
    paxRoute({ auth: account, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/basemaps/v1alpha1/{id}/drafts/{versionId}/blobs",
      operationId: "basemaps.drafts.blobs.create",
      summary: "Start uploads of draft blobs.",
      description:
        "Returns a presigned PUT for each blob the draft doesn't hold yet. Each accepts exactly the bytes whose SHA-256 names it, for 5 minutes.",
      inputStructure: "detailed",
      successStatus: 201,
      successDescription: "Where to upload each missing blob.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...accountErrors,
    ...noSuchDraft,
    CONFLICT: { message: "The draft is being published or appealed, and takes no blobs." },
  })
  .input(z.object({ params: BasemapDraftParams, body: CreateBasemapBlobsRequest }))
  .output(CreateBasemapBlobsResponse);

export const getBlob = oc
  .meta(
    paxRoute({ auth: account, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/basemaps/v1alpha1/{id}/drafts/{versionId}/blobs/{sha256}",
      operationId: "basemaps.drafts.blobs.get",
      summary: "Download a draft blob.",
      description:
        "Streams one blob the draft holds: a tile's lossless pixels, or an uploaded file, whole or one range of it. Blobs never change, so the response is cached for a year.",
      inputStructure: "detailed",
      outputStructure: "detailed",
      responseBodyHint: "octet-stream",
      successDescription: "The blob, or the range asked for.",
    }),
  )
  .errors({ ...errors.badRequest, ...accountErrors, ...noSuchDraft })
  .input(z.object({ params: BasemapBlobParams, headers: BasemapBlobHeaders }))
  .output(BlobResponse);

export const keepBlobs = oc
  .meta(
    paxRoute({ auth: account, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/basemaps/v1alpha1/{id}/drafts/{versionId}/blobs/keep",
      operationId: "basemaps.drafts.blobs.keep",
      summary: "Keep uploaded draft blobs.",
      description:
        "Copies each uploaded blob the draft doesn't hold yet into the draft, checked against its hash, and records it in `asset_version_blobs`, where it counts toward its author's storage. Then the editor sends the commit that names them to the room.",
      inputStructure: "detailed",
      successDescription: "Every blob is kept.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...accountErrors,
    ...noSuchDraft,
    CONFLICT: { message: "The draft is being published or appealed, and takes no blobs." },
    UNPROCESSABLE_CONTENT: {
      message: "A blob wasn't uploaded, or its bytes aren't its hash's.",
    },
  })
  .input(z.object({ params: BasemapDraftParams, body: KeepBasemapBlobsRequest }))
  .output(z.strictObject({}));

// Publishing

export const publish = oc
  .meta(
    paxRoute({ auth: account, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/basemaps/v1alpha1/{id}/drafts/{versionId}/publish",
      operationId: "basemaps.drafts.publish",
      summary: "Publish a basemap draft.",
      description:
        "Starts a publish Workflow that freezes the draft's room at `seq`, its latest commit, and writes it out as a package; asking again for the same commit returns the same job. The package is checked with `checkPackage`, SafeSearch rates its color, and automod checks the version's text. When it succeeds, the draft is the asset's newest published version, with the same id, and never changes again.",
      inputStructure: "detailed",
      successStatus: 202,
      successDescription: "The publish job.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...accountErrors,
    ...noSuchDraft,
    CONFLICT: {
      message: "`seq` isn't the room's latest commit, or the draft is already publishing.",
    },
    UNPROCESSABLE_CONTENT: {
      message: "The draft is missing details it needs to publish, such as a title or a language.",
    },
  })
  .input(z.object({ params: BasemapDraftParams, body: PublishBasemapRequest }))
  .output(BasemapJob);

export const getJob = oc
  .meta(
    paxRoute({ auth: account, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/basemaps/v1alpha1/{id}/jobs/{jobId}",
      operationId: "basemaps.jobs.get",
      summary: "Get a basemap job.",
      description: "Reads a publish job's status from its Workflow instance.",
      inputStructure: "detailed",
      successDescription: "The job.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...accountErrors,
    NOT_FOUND: { message: "There's no job with that id on a basemap the caller can edit." },
  })
  .input(z.object({ params: BasemapJobParams }))
  .output(BasemapJob);

// Takedowns

const takedowns = staffAuth("assets.lead_moderate");

export const getFile = oc
  .meta(
    paxRoute({ auth: takedowns, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/basemaps/v1alpha1/files/{sha256}",
      operationId: "basemaps.files.get",
      summary: "Get a published basemap file.",
      description: "Returns one published file and every basemap version that lists it.",
      inputStructure: "detailed",
      successDescription: "The file and the basemap versions that share it.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...accountErrors,
    NOT_FOUND: { message: "No published file has that hash." },
  })
  .input(z.object({ params: BasemapFileParams }))
  .output(BasemapFileResponse);

export const deleteFile = oc
  .meta(
    paxRoute({ auth: takedowns, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "DELETE",
      path: "/api/basemaps/v1alpha1/files/{sha256}",
      operationId: "basemaps.files.delete",
      summary: "Delete a published basemap file.",
      description:
        "Deletes the file's bytes at once, for moderation, safety, or copyright. Every basemap version that lists it breaks, along with the games on them, and nothing repairs them.",
      inputStructure: "detailed",
      successDescription: "The file, now removed, and the basemap versions it broke.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...accountErrors,
    NOT_FOUND: { message: "No published file has that hash." },
    CONFLICT: { message: "The file was already deleted." },
  })
  .input(z.object({ params: BasemapFileParams, body: DeleteBasemapFileRequest }))
  .output(BasemapFileResponse);
